import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../../api/apiClient';

/* ────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────── */

const isActive = (item) => item.status !== 0;

const byOrder = (arr) =>
  arr
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const diff = (a.item.order ?? 0) - (b.item.order ?? 0);
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map(({ item }) => item);

/* ────────────────────────────────────────────────────────────────────────
 * Fallback content — used whenever the API call fails, returns a non-OK
 * status, or returns a payload with no usable footer section. Keeps the
 * shape identical to what the live API returns (linkSections / contact /
 * legal) so the render logic below never has to branch on where the data
 * came from.
 * ──────────────────────────────────────────────────────────────────────── */

const FALLBACK_FOOTER = {
  enabled: true,
  logo: {
    src: 'https://fplogoimages.withfloats.com/actual/69307aedfe3cec81a85953aa.png',
    alt: 'Dada Gujar Hospital Logo',
  },
  tagline: "Compassionate, personalized women's healthcare at every life stage.",
  newsletter: {
    heading: 'Stay Informed',
    subtext: 'Subscribe to get health tips and updates from us',
    placeholder: 'Your email',
  },
  linkSections: [
    {
      id: 'care',
      heading: 'Care',
      order: 0,
      status: 1,
      links: [
        { id: 'care-1', label: 'Conditions', href: '/conditions', order: 0, status: 1 },
        { id: 'care-2', label: 'Services', href: '/services', order: 1, status: 1 },
        { id: 'care-3', label: 'Programs', href: '/programs', order: 2, status: 1 },
        { id: 'care-4', label: 'Life Stages', href: '/lifeStages', order: 3, status: 1 },
      ],
    },
    {
      id: 'resources',
      heading: 'Resources',
      order: 1,
      status: 1,
      links: [
        { id: 'res-1', label: 'Guides', href: '/resource', order: 0, status: 1 },
        { id: 'res-2', label: 'Blogs', href: '/resource', order: 1, status: 1 },
        { id: 'res-3', label: 'Assessments', href: '/resource', order: 2, status: 1 },
        { id: 'res-4', label: "FAQ's", href: '/resource', order: 3, status: 1 },
      ],
    },
    {
      id: 'company',
      heading: 'Company',
      order: 2,
      status: 1,
      links: [
        { id: 'co-1', label: 'About Us', href: '/about', order: 0, status: 1 },
        { id: 'co-2', label: 'Doctors', href: '/about', order: 1, status: 1 },
        { id: 'co-3', label: 'Careers', href: '/about', order: 2, status: 1 },
        { id: 'co-4', label: 'Contact', href: '/about', order: 3, status: 1 },
      ],
    },
  ],
  contact: {
    heading: 'Contact',
    phone: '08043694807',
    email: 'frontdesk.ddgh@gmail.com',
    addressLine1: 'Dr. Dada Gujar Hospital Sr. No. 165-A Plot No. 159-173,',
    addressLine2: 'Opposite Sane Guruji school, Malwadi , Hadapsar, Pune.',
    href: null,
  },
  partnership: {
    label: 'Partnership',
    logos: [],
  },
  legal: {
    copyright: 'Copyright 2026 © Avexa Systems Pvt. Ltd.',
    links: [
      { id: 'legal-1', label: 'Privacy', href: '#', order: 0, status: 1 },
      { id: 'legal-2', label: 'Cookies', href: '#', order: 1, status: 1 },
      { id: 'legal-3', label: 'Terms of service', href: '#', order: 2, status: 1 },
    ],
  },
};

/* ────────────────────────────────────────────────────────────────────────
 * Reusable column for the dynamic link sections — same heading+links
 * shape, rendered with whatever links survive the status/order pass.
 * ──────────────────────────────────────────────────────────────────────── */

function FooterLinkColumn({ heading, links }) {
  return (
    <div className="flex flex-col gap-3">
      <h4
        className="ava-text-subheading underline underline-offset-4"
        style={{ color: 'var(--white)' }}
      >
        {heading}
      </h4>

      <ul className="flex flex-col gap-2">
        {links.map((link) => {
          const isExternal = /^https?:\/\//.test(link.href);

          return (
            <li key={link.id}>
              <a
                href={link.href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="ava-text-footer transition-colors duration-200"
                style={{ color: 'white' }}
              >
                {link.label}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const Footer = () => {
  const [footer, setFooter] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether we're showing the fallback content because the API call
  // failed or returned nothing usable. Doesn't block rendering — it's only
  // used if you want to surface a subtle "offline" indicator later.
  const [usingFallback, setUsingFallback] = useState(false);

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {

    setFooter(FALLBACK_FOOTER);
    setUsingFallback(true);
    setLoading(false);
    // let cancelled = false;

    // (async () => {
    //   try {
    //     const res = await fetch(`${BASE_URL}/api/femcare/`);
    //     const json = await res.json().catch(() => ({}));

    //     if (!res.ok) {
    //       throw new Error(json?.error || `Request failed (${res.status})`);
    //     }

    //     const fetchedFooter = json?.sections?.footer;

    //     // Treat a missing/empty/disabled footer payload the same as a
    //     // failed request — fall back rather than rendering nothing.
    //     if (!fetchedFooter || !fetchedFooter.enabled) {
    //       throw new Error('Footer payload missing or disabled');
    //     }

    //     if (!cancelled) {
    //       setFooter(fetchedFooter);
    //       setUsingFallback(false);
    //     }
    //   } catch (e) {
    //     console.error('Footer: falling back to default content —', e.message);
    //     if (!cancelled) {
    //       setFooter(FALLBACK_FOOTER);
    //       setUsingFallback(true);
    //     }
    //   } finally {
    //     if (!cancelled) setLoading(false);
    //   }
    // })();

    // return () => {
    //   cancelled = true;
    // };
  }, []);

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (!email.trim()) return;
  //   setSubmitted(true);
  //   setEmail('');
  // };

  // Only genuinely nothing-to-show while the very first fetch attempt is
  // still in flight. Once it settles (success or failure) `footer` is
  // always populated, either from the API or from FALLBACK_FOOTER.
  if (loading || !footer) return null;

  const {
    logo,
    tagline,
    newsletter,
    linkSections = [],
    contact,
    partnership,
    legal,
  } = footer;

  const columns = byOrder(linkSections.filter(isActive));
  const legalLinks = byOrder((legal?.links || []).filter(isActive));

  return (
    <footer className="w-full">
      {/* Main Footer */}
      <div className="w-full ava-footer-bg">
        <div className="mx-auto px-6 md:px-8 py-10 md:py-14">
          <div
            className="flex flex-wrap justify-between"
            style={{ gap: '2rem' }}
          >
            {/* Logo */}
            <div className="flex flex-col gap-4">
              <a href="/" className="inline-flex items-center gap-3 w-fit">
                <img
                  src={logo?.src}
                  alt={logo?.alt}
                  className="h-16 md:h-20 w-auto"
                />
              </a>

              <p
                className="ava-text-body"
                style={{
                  maxWidth: '200px',
                  color: 'var(--white)',
                }}
              >
                {tagline}
              </p>
            </div>

            {/* Dynamic Sections */}
            {columns.map((section) => (
              <FooterLinkColumn
                key={section.id}
                heading={section.heading}
                links={byOrder((section.links || []).filter(isActive))}
              />
            ))}

            {/* Contact */}
            <div className="flex flex-col gap-3">
              <h4
                className="ava-text-subheading underline underline-offset-4"
                style={{ color: 'var(--white)' }}
              >
                {contact?.heading}
              </h4>

              <ul className="flex flex-col gap-3">
                <li className="flex items-start gap-2">
                  <img
                    src="/images/VectorMobile.svg"
                    alt="Phone"
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                  />

                  <a
                    href={`tel:${contact?.phone?.replace(/\s/g, '')}`}
                    className="ava-text-footer"
                    style={{ color: 'white' }}
                  >
                    {contact?.phone}
                  </a>
                </li>

                <li className="flex items-start gap-2">
                  <img
                    src="/images/GroupMail.svg"
                    alt="Email"
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                  />

                  <a
                    href={`mailto:${contact?.email}`}
                    className="ava-text-footer"
                    style={{ color: 'white' }}
                  >
                    {contact?.email}
                  </a>
                </li>

                <li className="flex items-start gap-2">
                  <img
                    src="/images/GroupLocation.svg"
                    alt="Location"
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                  />

                  {contact?.href ? (
                    <a
                      href={contact.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ava-text-footer hover:underline max-w-[260px] inline-block whitespace-normal break-words"
                      style={{ color: 'white' }}
                    >
                      {contact.addressLine1}
                      <br />
                      {contact.addressLine2}
                    </a>
                  ) : (
                    <span
                      className="ava-text-footer"
                      style={{ color: 'white' }}
                    >
                      {contact?.addressLine1}
                      <br />
                      {contact?.addressLine2}
                    </span>
                  )}
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter (optional) */}
          {/* Uncomment if you want to use it later

          <div className="mt-10">
            <h3>{newsletter?.heading}</h3>
            <p>{newsletter?.subtext}</p>

            <form onSubmit={handleSubmit}>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={newsletter?.placeholder}
              />
              <button type="submit">Subscribe</button>
            </form>

            {submitted && <p>Subscribed successfully!</p>}
          </div>

          */}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="w-full ava-footer-bg">
        <div className="px-6 md:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="ava-text-footer text-white opacity-90 text-center sm:text-left">
              {legal?.copyright}
            </p>

            <div className="flex items-center gap-1 ava-text-footer text-white opacity-90">
              {legalLinks.map((link, index) => {
                const isExternal = /^https?:\/\//.test(link.href);

                return (
                  <React.Fragment key={link.id}>
                    {index > 0 && (
                      <span className="opacity-60 px-1">|</span>
                    )}

                    <a
                      href={link.href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      className="hover:underline transition-opacity hover:opacity-100"
                    >
                      {link.label}
                    </a>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;